import { useState, useLayoutEffect } from 'react';

export const useVisibleChildren = (
  containerRef: React.RefObject<HTMLElement>,
  wrapperRef: React.RefObject<HTMLElement>,
  overflowLabelRef: React.RefObject<HTMLElement>,
  totalItems: number,
  lines: 1 | 2
): {
  hasMeasured: boolean;
  hiddenCount: number;
  visibleCount: number;
} => {
  const [visibleCount, setVisibleCount] = useState(totalItems);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [firstChildOverflow, setFirstChildOverflow] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const overflowLabel = overflowLabelRef.current;
    if (!container || !wrapper || !overflowLabel) return;

    const recompute = (): void => {
      if (hasMeasured) return;
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const children = Array.from(wrapper.children) as HTMLElement[];
      if (children.length === 0) {
        setVisibleCount(0);
        setHasMeasured(true);
        return;
      }
      const overflowLabelWidth = overflowLabel.offsetWidth;
      const containerTop = container.getBoundingClientRect().top;
      const lineTops: number[] = [];
      const lineBottoms: number[] = [];
      let visible = 0;
      let isFirstChildOverflown = false;

      if (lines === 1 && !firstChildOverflow) {
        const containerHeight = container.clientHeight;
        const firstChild = children[0];
        const childHeight = firstChild.offsetHeight;
        isFirstChildOverflown = visible === 0 && childHeight > containerHeight;
        if (isFirstChildOverflown) {
          visible++;
          setFirstChildOverflow(true);
        }
      }

      let lastVisibleIndex = 0;
      let childOverflowed = false;
      if (lines === 2 || (lines === 1 && !isFirstChildOverflown)) {
        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const childTop = child.getBoundingClientRect().top - containerTop;
          const childBottom =
            child.getBoundingClientRect().bottom - containerTop;
          if (!lineTops.includes(childTop)) {
            lineTops.push(childTop);
          }
          if (!lineBottoms.includes(childBottom)) {
            lineBottoms.push(childBottom);
          }
          const startLineNumber = lineTops.indexOf(childTop);
          const finishLineNumber = lineBottoms.indexOf(childBottom);

          // check if child starts and ends within allowed lines
          if (startLineNumber < lines && finishLineNumber < lines) {
            visible++;
          } else {
            lastVisibleIndex = visible - 1;
            if (i !== children.length - 2) {
              childOverflowed = true;
            }
            break;
          }
        }
        let cutWidth = 0;
        // do this only if last visible child is not the
        // last child (the overflow label is the last one so we check the one before)
        // or if there is overflow
        if (childOverflowed || children.length !== lastVisibleIndex) {
          for (let i = lastVisibleIndex; i > 0; i--) {
            const child = children[i];

            cutWidth += child.offsetWidth;
            visible--;

            if (cutWidth > overflowLabelWidth) {
              break;
            }
          }
        }
      }

      setVisibleCount(visible);
      setHasMeasured(true);
    };

    recompute();

    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [
    containerRef,
    wrapperRef,
    overflowLabelRef,
    totalItems,
    lines,
    firstChildOverflow,
    hasMeasured,
  ]);

  const hiddenCount = totalItems - visibleCount;
  return { hasMeasured, hiddenCount, visibleCount };
};
